/**
 * personModalConfigApi — Person-Modal deep-clean config surfaces (Contract C5).
 *
 * Slot TL (admin-panel) consumes the four config routes that Slot TR
 * (`apps/PCBJxChangeApi`) owns. Per the fix-plan contract
 * (`docs/operations/person-modal-inventory/01-FIX-PLAN-AND-CONTRACTS.md` §C5)
 * these routes are:
 *
 *   GET   /v3/config/cip-id-priority                 — CipIdPriorityRule[] (single GLOBAL list)
 *   POST  /v3/admin/cip-id-priority/reorder          — {idTypeCodes[], legacyProcedureCitation}
 *   POST  /v3/admin/cip-id-priority/upsert           — single-row active/citation write
 *   GET   /v3/config/conflict-rules                  — ConflictRule[]
 *   PATCH /v3/admin/conflict-rules/{ruleId}          — {severity?, isActive?}
 *   GET   /v3/config/entity-rule-gate-enforcements   — (already typed on client.retail.config — reused)
 *   GET   /v3/config/gate-keys                       — (already typed on client.retail.config — reused)
 *   POST  /v3/admin/entity-rule-gate-enforcements/upsert — gate-strategy write
 *
 * Why a LOCAL service and not typed methods on `@retail-ops/api-client`:
 * Slot TL owns `apps/admin-panel/**` ONLY (fix-plan §1). The api-client
 * package is shared and the cip-id-priority / conflict-rules / gate-
 * enforcement-write routes are not yet declared there (TR ships them on
 * its own branch). So we call through the public `client.http` transport
 * against the documented contract paths. The transport still injects
 * `x-functions-key`, the MSAL bearer, and `x-pcb-officer-code` (from
 * ApiClientContext) automatically — identical auth to every other admin
 * editor. When TR's typed methods land, the orchestrator can swap these
 * call sites for `client.retail.admin.*` with no behavior change.
 *
 * Routes that are not live in this branch yet return 404/501/503; callers
 * use `isRouteNotLive()` to render a "route pending (TR)" banner instead of
 * a hard error, while still proving the read + write wiring end-to-end.
 *
 * The DI model-binding editor (task 3) is NOT here — it reads/writes the
 * already-shipped SystemSettings routes via `client.retail.config.systemSettingsRows()`
 * + `client.retail.admin.systemSettings.upsert()` directly.
 */

import {
  ApiError,
  ADMIN_AUTH_TOKEN_SENTINEL,
  buildAdminAuthHeaders,
  type ApiClient,
  type ApiResult,
} from '@retail-ops/api-client';

// ─── Contract C5 row shapes ────────────────────────────────────────────────

/** Severity tier for a cross-document conflict check (brief §2.6). */
export type ConflictSeverity = 'major' | 'minor' | 'info';

/**
 * One acceptable CIP ID type in the single GLOBAL slot-machine priority order
 * (Bill 2026-05-27: rank is global, not per-cohort — seeded F254-code ascending).
 * Drives `assignCipSlots` (Slot BL): the highest-ranked ACTIVE captured photo-ID
 * wins CIP ID1, the next wins ID2 (Passport→ID1, DL bumps→ID2). Per-cohort
 * behavior emerges for free — an NRA never captures a DL, so the Passport is the
 * highest captured ID and lands ID1. `idTypeCode` is a Core Director Field 254
 * code (catalog domain `IdVerifyCode`).
 */
export interface CipIdPriorityRule {
  /** Server PK when exposed; `idTypeCode` is the natural key (one row per F254 code). */
  id?: number;
  idTypeCode: string;
  /** Optional server-joined label; the editor resolves from the F254 catalog when absent. */
  idTypeLabel?: string;
  /** 1-based priority; lower = higher priority. Editor derives this from list order. */
  rank: number;
  isActive: boolean;
  /** Cite-or-defer (CLAUDE.md rule #1): the legacy source for this ordering. */
  legacyProcedureCitation?: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

/**
 * One of the 40 cross-document conflict checks (brief §3, Tiers A/B/C).
 * `scope` + `label` are read-only catalog metadata; admin toggles `isActive`
 * and re-tiers `severity`. No DELETE — deactivate only (no-system-breaking-edits).
 */
export interface ConflictRule {
  ruleId: string;
  label: string;
  /** Read-only: what the rule compares (e.g. 'passportNumber', 'dob', 'name'). */
  scope: string;
  /** Grouping tier (A/B/C) — present on the seeded catalog; editor groups by it. */
  tier?: string;
  /** The seeded default severity (shown so the admin sees drift from default). */
  defaultSeverity: ConflictSeverity;
  /** The current effective severity (editable). */
  severity: ConflictSeverity;
  isActive: boolean;
  description?: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

/**
 * One admin-configured in-market ZIP (dbo.MarketAreaZip; Bill 2026-06-13).
 * Drives the FraudPoint Moderate-band in/out-of-market collect rule
 * (FraudPointRiskGateService): when the active set is non-empty the gate
 * decides market by ZIP membership; when empty it falls back to the legacy
 * state rule (out-of-state = out-of-market). County/City/Label are
 * informational; only ZIP membership is load-bearing. Deactivate, never delete.
 */
export interface MarketAreaZipRow {
  zip: string;
  county?: string | null;
  city?: string | null;
  label?: string | null;
  isActive: boolean;
  sortOrder?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface MarketAreaZipUpsertBody {
  zip: string;
  county?: string | null;
  city?: string | null;
  label?: string | null;
  isActive?: boolean;
  sortOrder?: number | null;
}

/** A row of dbo.GateKeys (read via the existing /v3/config/gate-keys route). */
export interface GateKeyRow {
  key: string;
  displayName: string;
  category: string;
  scope: string;
  /** JSON array string of allowed EnforcementStrategy values. */
  allowedStrategiesJson: string;
  defaultStrategy: string;
  defaultDeferralDays?: number | null;
  description?: string | null;
  isActive: boolean;
}

/**
 * A per-(CustomerType × GateKey) enforcement-strategy override
 * (dbo.EntityRuleGateEnforcement). Read via the existing
 * /v3/config/entity-rule-gate-enforcements route; written via the admin upsert.
 */
export interface GateEnforcementRow {
  customerType: string;
  gateKey: string;
  enforcementStrategy: string;
  deferralDays?: number | null;
  /** NOT NULL at the SQL level — every row cites a ProcedureCitation. */
  legacyProcedureCitation: string;
  seededDefault?: boolean;
  overrideReason?: string | null;
  isActive: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

// ─── Write bodies (documented contract — TR confirms exact field names) ─────

export interface CipIdPriorityReorderBody {
  /** idTypeCodes in desired order; index → rank (1-based). Single global list. */
  idTypeCodes: ReadonlyArray<string>;
  legacyProcedureCitation: string;
}

export interface CipIdPriorityUpsertBody {
  idTypeCode: string;
  isActive?: boolean;
  rank?: number;
  legacyProcedureCitation: string;
}

export interface ConflictRulePatchBody {
  severity?: ConflictSeverity;
  isActive?: boolean;
}

export interface GateEnforcementUpsertBody {
  customerType: string;
  gateKey: string;
  enforcementStrategy: string;
  deferralDays?: number | null;
  legacyProcedureCitation: string;
  overrideReason?: string | null;
  isActive?: boolean;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * True when the failure means "TR hasn't shipped this route to this branch
 * yet" (404 / 501 / 503 / config-route-not-seeded). Editors render a
 * non-fatal "pending" banner for these and a hard error for everything else.
 */
export function isRouteNotLive(e: unknown): boolean {
  if (ApiError.isApiError(e)) {
    const s = e.httpStatus;
    return s === 404 || s === 501 || s === 503;
  }
  return false;
}

export function errMessage(e: unknown): string {
  if (ApiError.isApiError(e)) return e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}

function adminHeaders(): Record<string, string> {
  // Empty/sentinel bypass-key → header omitted; MSAL bearer + officer-code
  // (auto-injected by the transport) carry auth. Same path as every admin editor.
  return buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL);
}

// ─── Service ─────────────────────────────────────────────────────────────────

export class PersonModalConfigApi {
  public constructor(private readonly client: ApiClient) {}

  private get http() {
    return this.client.http;
  }

  // ── Task 1: CIP-ID priority ──────────────────────────────────────────────

  listCipIdPriority(signal?: AbortSignal): Promise<ApiResult<ReadonlyArray<CipIdPriorityRule>>> {
    return this.http.get<ApiResult<ReadonlyArray<CipIdPriorityRule>>>(
      '/v3/config/cip-id-priority',
      { signal },
    );
  }

  reorderCipIdPriority(
    body: CipIdPriorityReorderBody,
    signal?: AbortSignal,
  ): Promise<ApiResult<unknown>> {
    return this.http.post<ApiResult<unknown>>('/v3/admin/cip-id-priority/reorder', {
      headers: adminHeaders(),
      body,
      signal,
    });
  }

  upsertCipIdPriority(
    body: CipIdPriorityUpsertBody,
    signal?: AbortSignal,
  ): Promise<ApiResult<unknown>> {
    return this.http.post<ApiResult<unknown>>('/v3/admin/cip-id-priority/upsert', {
      headers: adminHeaders(),
      body,
      signal,
    });
  }

  // ── Task 2: Conflict rules ────────────────────────────────────────────────

  listConflictRules(signal?: AbortSignal): Promise<ApiResult<ReadonlyArray<ConflictRule>>> {
    return this.http.get<ApiResult<ReadonlyArray<ConflictRule>>>(
      '/v3/config/conflict-rules',
      { signal },
    );
  }

  patchConflictRule(
    ruleId: string,
    body: ConflictRulePatchBody,
    signal?: AbortSignal,
  ): Promise<ApiResult<unknown>> {
    return this.http.patch<ApiResult<unknown>>(
      `/v3/admin/conflict-rules/${encodeURIComponent(ruleId)}`,
      { headers: adminHeaders(), body, signal },
    );
  }

  // ── Market-Area ZIPs (FraudPoint in-market set, Bill 2026-06-13) ──────────

  /** All ZIP rows incl. inactive (admin list). */
  listMarketAreaZips(signal?: AbortSignal): Promise<ApiResult<ReadonlyArray<MarketAreaZipRow>>> {
    return this.http.get<ApiResult<ReadonlyArray<MarketAreaZipRow>>>(
      '/v3/admin/market-area-zips',
      { headers: adminHeaders(), signal },
    );
  }

  upsertMarketAreaZip(
    body: MarketAreaZipUpsertBody,
    signal?: AbortSignal,
  ): Promise<ApiResult<unknown>> {
    return this.http.post<ApiResult<unknown>>('/v3/admin/market-area-zips/upsert', {
      headers: adminHeaders(),
      body,
      signal,
    });
  }

  deactivateMarketAreaZip(zip: string, signal?: AbortSignal): Promise<ApiResult<unknown>> {
    return this.http.post<ApiResult<unknown>>('/v3/admin/market-area-zips/deactivate', {
      headers: adminHeaders(),
      body: { zip },
      signal,
    });
  }

  // ── Task 4: Gate enforcement (reads reuse the shipped /v3/config routes) ──

  async listGateEnforcements(signal?: AbortSignal): Promise<ReadonlyArray<GateEnforcementRow>> {
    const resp = await this.client.retail.config.entityRuleGateEnforcements({ signal });
    if (!resp.success || !Array.isArray(resp.data)) {
      throw new Error(resp.error ?? 'Failed to load gate enforcements.');
    }
    return resp.data.map(coerceGateEnforcementRow);
  }

  async listGateKeys(signal?: AbortSignal): Promise<ReadonlyArray<GateKeyRow>> {
    const resp = await this.client.retail.config.gateKeys({ signal });
    if (!resp.success || !Array.isArray(resp.data)) {
      throw new Error(resp.error ?? 'Failed to load gate keys.');
    }
    return resp.data.map(coerceGateKeyRow);
  }

  upsertGateEnforcement(
    body: GateEnforcementUpsertBody,
    signal?: AbortSignal,
  ): Promise<ApiResult<unknown>> {
    return this.http.post<ApiResult<unknown>>(
      '/v3/admin/entity-rule-gate-enforcements/upsert',
      { headers: adminHeaders(), body, signal },
    );
  }
}

// ─── Loose-row coercion (the /v3/config Tier-1 reads return Record<string,unknown>) ─

function s(v: unknown): string {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}
function sOpt(v: unknown): string | null {
  return v == null ? null : s(v);
}
function b(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') return ['true', '1', 'yes', 'on'].includes(v.toLowerCase());
  return false;
}
function nOpt(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function coerceGateEnforcementRow(r: Record<string, unknown>): GateEnforcementRow {
  const seeded = r.seededDefault ?? r.SeededDefault;
  return {
    customerType: s(r.customerType ?? r.CustomerType),
    gateKey: s(r.gateKey ?? r.GateKey),
    enforcementStrategy: s(r.enforcementStrategy ?? r.EnforcementStrategy),
    deferralDays: nOpt(r.deferralDays ?? r.DeferralDays),
    legacyProcedureCitation: s(r.legacyProcedureCitation ?? r.LegacyProcedureCitation),
    seededDefault: seeded == null ? undefined : b(seeded),
    overrideReason: sOpt(r.overrideReason ?? r.OverrideReason),
    isActive: b(r.isActive ?? r.IsActive ?? true),
    updatedAt: sOpt(r.updatedAt ?? r.UpdatedAt),
    updatedBy: sOpt(r.updatedBy ?? r.UpdatedBy),
  };
}

function coerceGateKeyRow(r: Record<string, unknown>): GateKeyRow {
  return {
    key: s(r.key ?? r.Key),
    displayName: s(r.displayName ?? r.DisplayName),
    category: s(r.category ?? r.Category),
    scope: s(r.scope ?? r.Scope),
    allowedStrategiesJson: s(r.allowedStrategiesJson ?? r.AllowedStrategiesJson ?? '[]'),
    defaultStrategy: s(r.defaultStrategy ?? r.DefaultStrategy),
    defaultDeferralDays: nOpt(r.defaultDeferralDays ?? r.DefaultDeferralDays),
    description: sOpt(r.description ?? r.Description),
    isActive: b(r.isActive ?? r.IsActive ?? true),
  };
}

/** Canonical EnforcementStrategy enum (EntityRuleGateEnforcement.cs:14-19). */
export const ENFORCEMENT_STRATEGIES = [
  { value: 'required-hard-stop', label: 'Hard stop — block until satisfied' },
  { value: 'warn-only', label: 'Warn only — warn but don’t block' },
  { value: 'deferred-N-days-post-acctadd', label: 'Deferred N days — track deadline post-AcctAdd' },
  { value: 'officer-override', label: 'Officer override — code + reason bypasses' },
] as const;

export type EnforcementStrategyValue = (typeof ENFORCEMENT_STRATEGIES)[number]['value'];
